import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import { roleMatches } from '../../utils/rolePermissions';
import StaffTrainingHub from './StaffTrainingHub';

/**
 * Standalone ground-staff training / certificates page when the full Staff details suite is off.
 * Assignments stay available only when the suite is on (via Staff details → Training).
 */
function StaffTrainingPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [status, setStatus] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const stRes = await axios.get('/api/hrms/status');
      const st = stRes.data;
      setStatus(st);

      if (st?.enabled && st?.canAccess) {
        return;
      }
      if (!st?.canAccessStaffTraining) {
        return;
      }

      const empRes = await axios.get('/api/hrms/staff-training/employees');
      setEmployees(empRes.data || []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load Staff training', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const canAssign = useMemo(() => {
    if (!status) return false;
    if (typeof status.canAssign === 'boolean') return status.canAssign;
    return status.canViewAll || (Array.isArray(status.managedDepartments) && status.managedDepartments.length > 0);
  }, [status]);

  const staffCertsSelfId = useMemo(() => {
    if (!user?._id) return null;
    const isStaffManager = roleMatches(user.role, [
      ROLES.SUPER_ADMIN,
      ROLES.ACCOUNTABLE_MANAGER,
      ROLES.HR_MANAGER,
      ROLES.SAFETY_COFS,
      ROLES.SAFETY_MANAGER,
      ROLES.SAFETY_OFFICER
    ]);
    if (isStaffManager || canAssign || status?.canViewAll || status?.canAssignTraining) return null;
    const managed = status?.managedDepartments;
    if (Array.isArray(managed) && managed.length > 0) return null;
    return user._id;
  }, [user, canAssign, status]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status?.enabled && status?.canAccess) {
    return <Navigate to="/hrms?tab=training&section=certificates" replace />;
  }

  if (!status?.canAccessStaffTraining) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <StaffTrainingHub
        employees={employees}
        selfCrewId={staffCertsSelfId}
        showAssignments={false}
        title="Staff training"
        subtitle="Profile certificates and trainings for ground staff. Enable Staff details for time-bound assignments, roster, and attendance."
      />
    </Box>
  );
}

export default StaffTrainingPage;
